import { IGroup } from '@fluentui/react';
import { useFilterData } from '../../SearchFilter/filterContext';
import { groupByExternalDataSource } from './groupByExternalDataSource';
import { groupByKeyFromItem } from './groupByKeyFromItem';

/**
 * This is a hook that takes in a list of items and groups them based on the
 * provided groupByOption. It also applies the group collapse behavior.
 */
export const useSearchGroupList = <T,>(
  /** The current list of items that need grouped. */
  itemList: T[] | undefined,
  /** The list of currently collapsed groups. */
  collapsedGroups: any[],
  /** Whether or not all groups are collapsed. */
  isAllGroupsCollapsed: boolean
): IGroup[] | undefined => {
  const {
    commonFilterState: { groupBy: groupByOption, startDate, endDate },
  } = useFilterData();

  // Due to the rules of hooks, we have to lie to React here and
  // say that the hook is *always* defined, even though it's not.
  const useGroupDataHook = groupByOption?.useGroupData || (() => undefined);

  /** External data for grouping (if present) */
  const groupData = useGroupDataHook({ startDate, endDate }) || [];

  // Skip all group logic if there's no grouping mode selected.
  if (!groupByOption || !itemList) {
    return undefined;
  }

  // If we're using an external data source, group by that data.
  if (groupByOption?.useGroupData) {
    return groupByExternalDataSource<T>(
      itemList,
      groupData,
      groupByOption,
      isAllGroupsCollapsed,
      collapsedGroups
    );
  }

  // If we're grouping by a key, group by that key.
  if (
    !!groupByOption?.key &&
    groupByOption?.key !== 'none' &&
    itemList?.length > 0
  ) {
    return groupByKeyFromItem(
      itemList,
      groupByOption,
      isAllGroupsCollapsed,
      collapsedGroups
    );
  }
  // Returning undefined here indicates to the DetailsList that we aren't using grouping
  return undefined;
};
